import React from "react";
import { Link } from "react-router-dom";
import {
  FaDatabase,
  FaLandmark,
  FaMapMarkerAlt,
  FaGlobe,
  FaArrowRight,
  FaShieldAlt,
} from "react-icons/fa";

const footerSections = [
  {
    title: "Explore Data",
    links: [
      { to: "/", label: "Home" },
      { to: "/india-census-2011", label: "India Data" },
      { to: "/census-2027-upcoming", label: "Census Help Hub" },
      { to: "/census-2027-upcoming/self-enumeration-guide", label: "Self-Enumeration Guide" },
      { to: "/religion", label: "Religion Data" },
      { to: "/analysis/diversity-index", label: "Diversity Index" },
      { to: "/analysis/religion-gender-ratio", label: "Gender Ratio Analysis" },
      { to: "/analysis/religion-urban-rural", label: "Urban vs Rural Analysis" },
    ],
  },
  {
    title: "Religion Discovery",
    links: [
      { to: "/religion/uttar-pradesh", label: "Uttar Pradesh Religion" },
      { to: "/religion/madhya-pradesh", label: "Madhya Pradesh Religion" },
      { to: "/religion/odisha", label: "Odisha Religion" },
      { to: "/religion/rajasthan", label: "Rajasthan Religion" },
      { to: "/religion/maharashtra", label: "Maharashtra Religion" },
      { to: "/sitemap", label: "Religion Sitemap" },
    ],
  },
  {
    title: "Project & Legal",
    links: [
      { to: "/about-us", label: "About Us" },
      { to: "/contact", label: "Contact Us" },
      { to: "/data-correction", label: "Data Correction" },
      { to: "/privacy-policy", label: "Privacy Policy" },
      { to: "/terms-and-conditions", label: "Terms & Conditions" },
      { to: "/disclaimer", label: "Disclaimer" },
    ],
  },
];

const Footer = () => {
  return (
    <footer
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #020617 100%)",
        color: "#94a3b8",
        position: "relative",
      }}
    >
      <style>{`
        .footer-link {
          color: #94a3b8;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-block;
          text-decoration: none;
        }
        .footer-link:hover {
          color: #60a5fa !important;
          transform: translateX(4px);
        }
        .footer-card {
          background: rgba(30, 41, 59, 0.4);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.06);
          transition: all 0.3s ease;
          border-radius: 12px;
        }
        .footer-card:hover {
          border-color: rgba(96, 165, 250, 0.3);
          background: rgba(30, 41, 59, 0.6);
          transform: translateY(-2px);
        }
        .footer-badge {
          background: rgba(59, 130, 246, 0.08);
          color: #93c5fd;
          border: 1px solid rgba(59, 130, 246, 0.15);
          transition: all 0.2s ease;
        }
        .footer-badge:hover {
          background: rgba(59, 130, 246, 0.2);
          color: #ffffff;
          border-color: rgba(96, 165, 250, 0.4);
        }
      `}</style>

      {/* Neon Gradient Header Stripe */}
      <div style={{ height: "4px", background: "linear-gradient(90deg, #2563eb, #10b981)" }}></div>

      <div className="container py-5">
        <div className="row g-4 mb-5">
          <div className="col-xl-4 col-lg-5">
            <div className="d-flex align-items-center mb-4">
              <div
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{
                  width: "48px",
                  height: "48px",
                  background: "linear-gradient(135deg, #2563eb 0%, #10b981 100%)",
                  boxShadow: "0 0 15px rgba(37, 99, 235, 0.3)",
                }}
              >
                <FaMapMarkerAlt className="text-white fs-5" />
              </div>
              <div>
                <div className="fw-bold fs-3 text-white" style={{ letterSpacing: "-0.03em" }}>
                  <span style={{ color: "#3b82f6" }}>All India</span> Census
                </div>
                <div className="text-muted small" style={{ color: "#64748b" }}>Demographic & Religion Analytics</div>
              </div>
            </div>

            <p className="small mb-4" style={{ lineHeight: "1.7", color: "#94a3b8" }}>
              A specialized informational dashboard organizing district, subdistrict, and village-level religion datasets from Census of India 2011. Designed for clear analysis, visualization, and demographic exploration.
            </p>

            <div className="row g-3">
              <div className="col-sm-6">
                <div className="card footer-card h-100">
                  <div className="card-body p-3">
                    <div className="small mb-1 text-uppercase" style={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 600 }}>Coverage</div>
                    <div className="h4 fw-bold mb-1 text-white">4,800+</div>
                    <div className="small text-muted" style={{ fontSize: "0.75rem" }}>Mapped regional pages</div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6">
                <div className="card footer-card h-100">
                  <div className="card-body p-3">
                    <div className="small mb-1 text-uppercase" style={{ color: "#64748b", fontSize: "0.7rem", fontWeight: 600 }}>Data Accuracy</div>
                    <div className="h4 fw-bold mb-1 text-white">100%</div>
                    <div className="small text-muted" style={{ fontSize: "0.75rem" }}>Census 2011 compliant</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-8 col-lg-7">
            <div className="row g-4 justify-content-lg-end">
              {footerSections.map((section) => (
                <div className="col-md-4 col-sm-6" key={section.title}>
                  <h6 className="fw-bold mb-3 text-white" style={{ fontSize: "0.95rem", letterSpacing: "0.5px" }}>
                    {section.title}
                  </h6>
                  <ul className="list-unstyled small mb-0">
                    {section.links.map((item) => (
                      <li className="mb-2.5" key={item.to} style={{ marginBottom: "8px" }}>
                        <Link to={item.to} className="footer-link">
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="row g-4 mb-5">
          <div className="col-lg-4 col-md-6">
            <div className="card footer-card h-100">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center text-white">
                  <FaDatabase className="me-2 text-primary" />
                  Key Features
                </h6>
                <ul className="list-unstyled small mb-0" style={{ color: "#94a3b8" }}>
                  <li className="mb-2 d-flex align-items-start">
                    <FaArrowRight className="me-2 mt-1 text-success" style={{ fontSize: "0.7rem" }} />
                    Census 2027 forms, guides, and required data lists.
                  </li>
                  <li className="mb-2 d-flex align-items-start">
                    <FaArrowRight className="me-2 mt-1 text-success" style={{ fontSize: "0.7rem" }} />
                    Interactive donut charts and details card visualization.
                  </li>
                  <li className="mb-2 d-flex align-items-start">
                    <FaArrowRight className="me-2 mt-1 text-success" style={{ fontSize: "0.7rem" }} />
                    Granular subdistrict comparison metrics.
                  </li>
                  <li className="d-flex align-items-start">
                    <FaArrowRight className="me-2 mt-1 text-success" style={{ fontSize: "0.7rem" }} />
                    Literacy indices, gender gap, and diversity analysis.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-6">
            <div className="card footer-card h-100">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center text-white">
                  <FaGlobe className="me-2 text-primary" />
                  Popular States
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/religion/uttar-pradesh" className="badge rounded-pill text-decoration-none footer-badge">Uttar Pradesh</Link>
                  <Link to="/religion/maharashtra" className="badge rounded-pill text-decoration-none footer-badge">Maharashtra</Link>
                  <Link to="/religion/bihar" className="badge rounded-pill text-decoration-none footer-badge">Bihar</Link>
                  <Link to="/religion/gujarat" className="badge rounded-pill text-decoration-none footer-badge">Gujarat</Link>
                  <Link to="/religion/tamil-nadu" className="badge rounded-pill text-decoration-none footer-badge">Tamil Nadu</Link>
                  <Link to="/religion/karnataka" className="badge rounded-pill text-decoration-none footer-badge">Karnataka</Link>
                  <Link to="/religion/telangana" className="badge rounded-pill text-decoration-none footer-badge">Telangana</Link>
                  <Link to="/religion/rajasthan" className="badge rounded-pill text-decoration-none footer-badge">Rajasthan</Link>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4 col-md-12">
            <div className="card footer-card h-100" style={{ border: "1px dashed rgba(96, 165, 250, 0.3)" }}>
              <div className="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                  <h6 className="fw-bold mb-2 d-flex align-items-center text-white">
                    <FaShieldAlt className="me-2 text-warning" />
                    Submit Anomaly Report
                  </h6>
                  <p className="small mb-3" style={{ fontSize: "0.82rem", color: "#cbd5e1" }}>
                    Spotted a demographic discrepancy or an outdated administrative division? Report it to our data curation team directly.
                  </p>
                </div>
                <Link to="/data-correction" className="btn btn-primary btn-sm w-100 py-2 d-flex align-items-center justify-content-center gap-1 fw-bold" style={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.5px" }}>
                  File Correction Form
                  <FaArrowRight size={10} />
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 pt-4 border-top small" style={{ borderColor: "rgba(255, 255, 255, 0.06)", color: "#64748b" }}>
          <div>
            © {new Date().getFullYear()} <span className="text-white fw-bold">allindiacensus.com</span>. All Rights Reserved. Not affiliated with the Govt of India.
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge border" style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", borderColor: "rgba(255, 255, 255, 0.08)", color: "#94a3b8" }}>
              <FaLandmark className="me-1 text-primary" />
              Census 2011 Archive
            </span>
            <span className="badge border" style={{ backgroundColor: "rgba(255, 255, 255, 0.03)", borderColor: "rgba(255, 255, 255, 0.08)", color: "#94a3b8" }}>
              Search-First Discovery
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
